import React, { FC } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ConfigProvider, Layout } from "antd";

import { isChrome, isSafari, isFirefox, isEdge, isMobile } from "react-device-detect";

import { NewsContextProvider } from "./context/NewsContext/NewsContext";
import { ImagesProvider } from "./context/ImagesProvider";
import { EventsContextProvider } from "./context/EventsContext/NewsContext";

import MainHeader from "./components/HeaderComponent";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/Routing";
import Login from "./components/Routing/Login";
import Sidebar from "./components/SidebarComponent";

import HomePage from "./pages/HomePage";
import AuthWarning from "./pages/Auth/AuthWarning";
import Logout from "./pages/Logout";

import MainContainer from "./App.style";
import { SidebarMenuProvider } from "./context/SidebarMenuProvider";
import useScreenWidth from "./utils/useScreenWidth";
import Warning from "./pages/Auth/Warning";
import MobileWarning from "./pages/Auth/MobileWarning";
import AddNews from "./pages/AddNews";
import AddEvents from "./pages/AddEvents";
import EditNews from "./pages/EditNews";
// import EditEvents from "./pages/EditEvents";
import NewsList from "./pages/NewsList";
import EventList from "./pages/EventList";
import DiscoveryList from "./pages/DiscoveryList";
import AddDiscovery from "./pages/AddDiscovery";
import EditDiscovery from "./pages/EditDiscovery";
import { DiscoveryContextProvider } from "./context/DiscoveryContext/DiscoveryContext";

interface LayoutContainerProps {
	children: React.ReactNode;
}

const WIDTH_MESSAGE = "ScreenWidthMessage";
const BROWSER_MESSAGE = "ScreenBrowserMessage";
const MOBILE_MESSAGE = "ScreenMobileMessage";

const ProtectedLayout: FC<LayoutContainerProps> = ({ children }) => {
	return (
		<ImagesProvider>
			<SidebarMenuProvider>
				<Layout>
					<MainHeader />
					<MainContainer>
						<Sidebar />
						{children}
					</MainContainer>
					<Footer />
				</Layout>
			</SidebarMenuProvider>
		</ImagesProvider>
	);
};

const App: FC = () => {
	const isScreenWidthSmall = useScreenWidth();

	if (isMobile) {
		return <MobileWarning queryString={MOBILE_MESSAGE} />;
	} else if (isChrome || isSafari || isFirefox || isEdge) {
		if (isScreenWidthSmall) {
			return <Warning queryString={WIDTH_MESSAGE} />;
		}
	} else {
		return <Warning queryString={BROWSER_MESSAGE} />;
	}


	return (
		<ConfigProvider
			theme={{
				token: {
					fontFamily: "Roboto, sans-serif",
					lineHeight: 1.38,
				},
			}}
		>
			<Router>
				<NewsContextProvider>
					<DiscoveryContextProvider>
						<EventsContextProvider>
							<Routes>
								<Route element={<ProtectedRoute />}>
									<Route
										path="/"
										element={(
											<ProtectedLayout>
												<HomePage />
											</ProtectedLayout>
										)}
									/>
									<Route
										path="/news"
										element={(
											<ProtectedLayout>
												<NewsList />
											</ProtectedLayout>
										)}
									/>
									<Route
										path="/add-news"
										element={(
											<ProtectedLayout>
												<AddNews />
											</ProtectedLayout>
										)}
									/>
									<Route
										path="/edit-news/:id"
										element={(
											<ProtectedLayout>
												<EditNews />
											</ProtectedLayout>
										)}
									/>

									<Route
										path="/events"
										element={(
											<ProtectedLayout>
												<EventList />
											</ProtectedLayout>
										)}
									/>
									<Route
										path="/add-events"
										element={(
											<ProtectedLayout>
												<AddEvents />
											</ProtectedLayout>
										)}
									/>
									{/* <Route
										path="/edit-events/:id"
										element={(
											<ProtectedLayout>
												<EditEvents />
											</ProtectedLayout>
										)}
									/> */}

									<Route
										path="/discovery"
										element={(
											<ProtectedLayout>
												<DiscoveryList />
											</ProtectedLayout>
										)}
									/>
									<Route
										path="/add-discovery"
										element={(
											<ProtectedLayout>
												<AddDiscovery />
											</ProtectedLayout>
										)}
									/>
									<Route
										path="/edit-discovery/:id"
										element={(
											<ProtectedLayout>
												<EditDiscovery />
											</ProtectedLayout>
										)}
									/>
								</Route>
								<Route path="/auth-warning" element={<AuthWarning />} />
								<Route path="/accounts/login" element={<Login />} />
								<Route path="/accounts/logout" element={<Logout />} />
							</Routes>
						</EventsContextProvider>
					</DiscoveryContextProvider>
				</NewsContextProvider>
			</Router>
		</ConfigProvider>
	);
};

export default App;
